import { QueryClient, useMutation } from '@tanstack/react-query';
import { RQAPICall } from 'store/APIClient';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { budgetProposalsPostProcessingMiddleware } from '../budgetProposalsMiddlewares';
import { createBudgetQueryKeys } from '../createBudgetQueryKeys';
import { useBudgetProposalsQuery } from './useCustomQuery';

type UpdateBudgetProposalPayloadT = {
    budgetProposalId: number;
    budgetProposal: { phase: string };
};

/** Hook to fetch budget proposals.  */
export const useGetBudgetProposals = (): {
    data?: BudgetProposalT[];
    isLoading: boolean;
} => {
    const { budgetProposalsKeys } = createBudgetQueryKeys();
    return useBudgetProposalsQuery(budgetProposalsKeys(), async () => {
        const response = await RQAPICall({
            endpoint: 'budget_proposals',
            method: 'GET',
            postProcessingCallback: budgetProposalsPostProcessingMiddleware,
        });

        return response.content.data;
    });
};

const updateBudgetProposal = async (payload: UpdateBudgetProposalPayloadT) => {
    const response = await RQAPICall({
        endpoint: `budget_proposals/${payload.budgetProposalId}`,
        body: payload.budgetProposal,
        method: 'PATCH',
    });
    return response.content;
};

export const useUpdateBudgetProposal = ({
    queryClient,
    bpId,
}: {
    queryClient: QueryClient;
    bpId: number;
}) => {
    const { budgetProposalsKeys } = createBudgetQueryKeys();
    return useMutation({
        mutationFn: (payload: UpdateBudgetProposalPayloadT) =>
            updateBudgetProposal(payload),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: budgetProposalsKeys() });
            queryClient.resetQueries({
                queryKey: budgetProposalsKeys({ budgetProposalId: bpId }),
            });

            const { inscriptionsKeys } = createBudgetQueryKeys();
            queryClient.resetQueries({ queryKey: inscriptionsKeys() });
        },
    });
};
